import type ExcelJS from "exceljs";

import { BRAND_LOGO_BASE64, LOGO_POSITION } from "./brandLogo";
import loadExcelJs from "./loadExcelJs";
import {
    applyStyle,
    headerRowStyle,
    infoRowStyle,
    labelStyle,
    logoCellStyle,
    subtitleFont,
    titleFont,
    valueStyle
} from "./theme";
import type { ResolvedSheet, WorkbookSpec } from "./types";

const INFO_ROW_NUMBER = 1;
const HEADER_ROW_NUMBER = 2;

function renderSheet(workbook: ExcelJS.Workbook, resolvedSheet: ResolvedSheet, logoImageId: number) {
    const sheet = workbook.addWorksheet(resolvedSheet.name);

    // Deliberately no `header` on the column defs: ExcelJS would then write the
    // headers into row 1, which is the branding band.
    sheet.columns = resolvedSheet.columns.map(column => ({
        key: column.key,
        width: column.width,
        style: column.role === "label" ? labelStyle : valueStyle
    }));
    sheet.views = [{ state: "frozen", ...resolvedSheet.freeze }];

    // Row 1: logo in A1, merged title/subtitle band across the rest.
    sheet.addRow(["", ""]);

    const infoRow = sheet.getRow(INFO_ROW_NUMBER);

    applyStyle(infoRow, infoRowStyle);
    applyStyle(infoRow.getCell(1), logoCellStyle);

    if (resolvedSheet.columns.length > 1) {
        sheet.mergeCells(INFO_ROW_NUMBER, 2, INFO_ROW_NUMBER, resolvedSheet.columns.length);
    }

    sheet.getCell(INFO_ROW_NUMBER, 2).value = {
        richText: [
            { font: titleFont, text: `${resolvedSheet.title} \r\n` },
            { font: subtitleFont, text: resolvedSheet.subtitle }
        ]
    };
    sheet.addImage(logoImageId, LOGO_POSITION);

    // Row 2: headers.
    sheet.addRow(resolvedSheet.columns.map(column => column.header));
    applyStyle(sheet.getRow(HEADER_ROW_NUMBER), headerRowStyle);

    // Rows 3+: only the first data row carries an explicit style. The rest use
    // ExcelJS's "i" (inherit from the row above), which avoids re-resolving a
    // style object per row and is materially faster on large exports.
    let isFirstDataRow = true;

    resolvedSheet.forEachRow(cells => {
        const row = sheet.addRow(cells, isFirstDataRow ? undefined : "i");

        if (!isFirstDataRow) {
            return;
        }

        applyStyle(row, valueStyle);

        for (let cellNumber = 1; cellNumber <= resolvedSheet.labelColumnCount; cellNumber += 1) {
            applyStyle(row.getCell(cellNumber), labelStyle);
        }

        isFirstDataRow = false;
    });

    return sheet;
}

export default async function buildWorkbook(spec: WorkbookSpec) {
    const excelJS = await loadExcelJs();
    const workbook = new excelJS.Workbook();
    const logoImageId = workbook.addImage({ base64: BRAND_LOGO_BASE64, extension: "png" });
    const sheets: Record<string, ExcelJS.Worksheet> = {};

    spec.sheets.forEach(resolvedSheet => {
        const sheet = renderSheet(workbook, resolvedSheet, logoImageId);

        sheets[resolvedSheet.name] = sheet;
        resolvedSheet.onRendered?.({ workbook, sheet, excelJS });
    });

    spec.onRendered?.({ workbook, sheets, excelJS });

    return workbook;
}
